import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { HiChevronDown } from "react-icons/hi";
import IFHeading from "../components/IF_Heading";
import { getCertification, getCertificationCache } from "../services";

function SafeJsonParse(str, fallback = []) {
    try {
        return JSON.parse(str);
    } catch {
        return fallback;
    }
}

function CertificationCard({ item, index }) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: index * 0.1, ease: "linear" }}
        >
            <div
                onClick={() => setIsOpen(!isOpen)}
                className={`cursor-pointer rounded-2xl p-5 border transition-colors duration-200 ${isOpen
                        ? "bg-pink/15 border-pink"
                        : "bg-[#12141e] border-white/10 hover:border-pink"
                    }`}
            >
                <div className="flex justify-between items-center">
                    <h4
                        className={`text-sm sm:text-base font-extrabold tracking-wide ${isOpen ? "text-pink" : "text-white"
                            }`}
                    >
                        {item.title.toUpperCase()}
                    </h4>
                    <HiChevronDown
                        className={`text-xl text-pink transition-transform duration-300 ${isOpen ? "rotate-180" : "rotate-0"
                            }`}
                    />
                </div>

                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.4, ease: "linear" }}
                            className="overflow-hidden"
                        >
                            <div className="pt-4 border-t border-white/10 mt-3">
                                <p className="text-xs sm:text-sm text-gray-300 leading-relaxed whitespace-pre-line">
                                    {item.description}
                                </p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
}

function LocalCarousel({ data = [] }) {
    if (!data || data.length === 0) return null;

    return (
        <div className="w-full flex justify-center px-4">
            <div className="w-full max-w-[1100px] rounded-2xl overflow-hidden">
                <Swiper
                    spaceBetween={20}
                    centeredSlides={true}
                    autoplay={{
                        delay: 4500,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                    }}
                    pagination={{ clickable: true }}
                    navigation={true}
                    modules={[Autoplay, Pagination, Navigation]}
                >
                    {data.map((i, idx) => (
                        <SwiperSlide key={idx}>
                            <div className="w-full aspect-video md:aspect-[21/9] rounded-2xl overflow-hidden bg-black relative">
                                <img
                                    src={process.env.REACT_APP_IMAGE_ALBUM_URL + i.filename}
                                    alt="Sertifikasi dan Workshop Pelatih I-FIT Indonesia"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#08090d]/50 to-transparent" />
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
}

export default function Certification() {
    const cached = getCertificationCache();
    const [data, setDatas] = useState(() => cached || { datas: [], album: [] });
    const [load, setLoad] = useState(() => !cached || !cached.datas || cached.datas.length === 0);

    useEffect(() => {
        getCertification().then((res) => {
            if (res && res.datas && res.datas.length > 0) {
                setDatas(res);
            }
            setLoad(false);
        });
    }, []);

    if (load || !data.datas || data.datas.length === 0) {
        return (
            <div className="min-h-[70vh] w-full flex items-center justify-center">
                <div className="w-12 h-12 rounded-full border-4 border-white/20 border-t-pink animate-spin" />
            </div>
        );
    }

    const workshops = SafeJsonParse(data.datas[0]?.value, []);

    return (
        <div className="flex flex-col w-full overflow-x-hidden">
            {/* Header Hero */}
            <section className="pt-[130px] lg:pt-[150px] pb-10 md:pb-16">
                <IFHeading as="h1">CERTIFICATION & WORKSHOP</IFHeading>
                <div className="mt-6">
                    <LocalCarousel data={data.album} />
                </div>
            </section>

            {/* Modules Grid */}
            <section className="py-8 md:py-16 px-4 md:px-8">
                <div className="max-w-[1200px] mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {workshops.map((p, index) => (
                            <CertificationCard key={index} item={p} index={index} />
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
